import { Link as MaterialLink } from '@material-ui/core';
import AssignmentIcon from '@material-ui/icons/Assignment';
import { makeStyles } from '@material-ui/styles';
import cn from 'classnames';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import mentorsAPI, { MentorVentureHistory } from '../../api/mentors';
import { MentorId } from '../../api/mentors';
import { AlertState, PageLoader, Text, Link } from '../../components/common';
import PopupIcon from '../../components/common/popup-icon';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { formatDate } from '../../utils/date';
import { isTablet } from '../../utils/functions';

const useStyles = makeStyles({
  loader: {
    width: '100%',
    minHeight: 400,
    maxHeight: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: { width: '100%' },
  row: {
    display: 'flex',
    alignItems: 'center',
    borderBottom: `1px solid ${COLORS.COLOR_GRAY_LIGHTENED_30}`,
    padding: '11px 12px 11px 0',
    '&:hover:not($headRow)': { background: COLORS.COLOR_GRAY_LIGHTENED_45 },
  },
  headRow: {
    padding: '13px 12px 15px 0',
    '& $leadTd': { padding: '0 16px' },
  },
  td: { padding: '0 10px', boxSizing: 'border-box' },
  dateTd: { minWidth: 140 },
  nameTd: {
    width: '100%',
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    overflow: 'hidden',
  },
  text: { color: COLORS.COLOR_BLUE_DARKENED_10 },
  link: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

function MentorVentureHistoryPage() {
  const classes = useStyles();
  const location = useLocation();
  const { mentorId } = useParams<{ mentorId: MentorId }>();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [historyVentures, setHistoryVentures] = useState<
    MentorVentureHistory[]
  >([]);

  const currentPath = useMemo(
    () => `${location.pathname}${location.search}`,
    [location.pathname, location.search],
  );

  useEffect(() => {
    async function loadHistoryVentures() {
      try {
        if (!mentorId) {
          throw new Error('Mentor ID is required');
        }

        setIsLoading(true);
        const ventures = await mentorsAPI.getHistoryVentures(mentorId);
        setHistoryVentures(ventures);
      } catch (error: any) {
        console.error(error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          'Internal server error';
        setLoadError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }

    loadHistoryVentures();
  }, [mentorId]);

  const stopPropagation = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  if (isLoading) {
    return (
      <div className={classes.loader}>
        <PageLoader />
      </div>
    );
  }

  if (!!loadError) {
    return (
      <div className={classes.loader}>
        <AlertState type='error'>{loadError}</AlertState>
      </div>
    );
  }

  if (historyVentures.length === 0) {
    return (
      <div className={classes.loader}>
        <Text variant='normal'>No assignments history yet...</Text>
      </div>
    );
  }

  return (
    <div className={cn(classes.container)}>
      <div className={cn(classes.row, classes.headRow)}>
        <div className={cn(classes.td, classes.nameTd)}>
          <Text variant='normal2' bold>
            Name
          </Text>
        </div>
        {!isTablet() && (
          <>
            <div className={cn(classes.td, classes.dateTd)}>
              <Text variant='normal2' bold>
                Start Date
              </Text>
            </div>
            <div className={cn(classes.td, classes.dateTd)}>
              <Text variant='normal2' bold>
                End Date
              </Text>
            </div>
          </>
        )}
      </div>

      {historyVentures.map((venture, index) => {
        const { id, ventureName, startDate, endDate, notes, ventureId } =
          venture;

        return (
          <div
            key={id}
            data-testid={`assigned-venture-${index}`}
            className={classes.row}>
            <div className={cn(classes.td, classes.nameTd)}>
              {ventureName ? (
                <>
                  <MaterialLink
                    onClick={stopPropagation}
                    component={Link}
                    className={classes.link}
                    to={{
                      pathname: getRoutePath(Pages.VENTURE_DETAILS, {
                        ventureId,
                      }),
                      state: { prevPath: currentPath },
                    }}>
                    <Text className={classes.text} variant='normal'>
                      {ventureName}
                    </Text>
                  </MaterialLink>
                  {!!notes && (
                    <PopupIcon
                      icon={<AssignmentIcon />}
                      content={notes}
                      testId='notes-icon'
                      isNoIcon={!notes}
                      emptyText='There is no notes available.'
                      enterTouchDelay={0}
                    />
                  )}
                </>
              ) : (
                <Text variant='normal' color={COLORS.COLOR_RED_BASE}>
                  Deleted
                </Text>
              )}
            </div>
            {!isTablet() && (
              <>
                <div className={cn(classes.td, classes.dateTd)}>
                  <Text variant='normal'>
                    {startDate ? formatDate(startDate) : ''}
                  </Text>
                </div>
                <div className={cn(classes.td, classes.dateTd)}>
                  <Text variant='normal'>
                    {endDate ? formatDate(endDate) : ''}
                  </Text>
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default MentorVentureHistoryPage;
